const PLAIN_TEXT_INJECTION_PATTERN = /<\s*\/?\s*(script|iframe|object|embed|svg|math|style|link|meta|base|form)\b|on[a-z]+\s*=|(?:javascript|vbscript)\s*:|data\s*:\s*text\/html|&#x?0*3c;|&#x?0*3e;|%3cscript|%3ciframe|%3csvg|%3cimg|%3cobject|%3cembed/i;
const PLAIN_TEXT_IMG_TAG_PATTERN = /<\s*\/?\s*img\b/i;
const CONTROL_CHARACTER_PATTERN = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/;

function toSafeString(value) {
  return value == null ? '' : String(value);
}

function matchesInjectionPattern(value) {
  const raw = toSafeString(value);
  if (!raw) return false;
  if (PLAIN_TEXT_INJECTION_PATTERN.test(raw) || PLAIN_TEXT_IMG_TAG_PATTERN.test(raw) || CONTROL_CHARACTER_PATTERN.test(raw)) {
    return true;
  }
  try {
    const decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
    return PLAIN_TEXT_INJECTION_PATTERN.test(decoded) || PLAIN_TEXT_IMG_TAG_PATTERN.test(decoded) || CONTROL_CHARACTER_PATTERN.test(decoded);
  } catch (_) {
    return false;
  }
}

/** Mirrors backend XssSanitizer::hasInjectionRisk for plain-text fields */
export function hasPlainTextInjectionRisk(value) {
  return matchesInjectionPattern(value);
}

/** Embed widget: allow script/iframe; flag handlers and dangerous URLs. */
export function hasEmbedInjectionRisk(value) {
  const raw = toSafeString(value);
  if (!raw) return false;

  if (/\s+on[a-zA-Z]+\s*=/i.test(raw)) return true;
  if (/(?:href|src|xlink:href|formaction)\s*=\s*["']?\s*(?:javascript|vbscript)\s*:/i.test(raw)) return true;
  if (/<\s*(?:object|embed|svg|math|base|form)\b/i.test(raw)) return true;
  if (CONTROL_CHARACTER_PATTERN.test(raw)) return true;

  return false;
}

export function getInjectionRiskMessage(fieldName) {
  return `Potentially unsafe input detected in ${fieldName}. Please remove HTML/script-like characters.`;
}

const DISPLAY_ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'SPAN', 'BR', 'SMALL', 'SUP', 'SUB',
]);

const DISPLAY_ALLOWED_ATTRS = {
  SPAN: new Set(['class']),
};

function stripDangerousElementAttrs(element) {
  Array.from(element.attributes).forEach((attr) => {
    const name = attr.name.toLowerCase();
    if (/^on[a-z]/.test(name)) {
      element.removeAttribute(attr.name);
      return;
    }
    const allowed = DISPLAY_ALLOWED_ATTRS[element.tagName];
    if (!allowed || !allowed.has(name)) {
      element.removeAttribute(attr.name);
      return;
    }
    if (/(?:javascript|vbscript)\s*:/i.test(attr.value)) {
      element.removeAttribute(attr.name);
    }
  });
}

function sanitizeHtmlNode(node) {
  const childNodes = Array.from(node.childNodes);
  for (const child of childNodes) {
    if (child.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }

    if (!DISPLAY_ALLOWED_TAGS.has(child.tagName)) {
      const fragment = document.createDocumentFragment();
      while (child.firstChild) {
        fragment.appendChild(child.firstChild);
      }
      child.replaceWith(fragment);
      sanitizeHtmlNode(node);
      continue;
    }

    stripDangerousElementAttrs(child);
    sanitizeHtmlNode(child);
  }
}

/**
 * Safe HTML for v-html: keeps basic formatting tags, strips XSS vectors.
 */
export function sanitizeHtmlForDisplay(html) {
  if (html == null || html === '') return '';

  const input = String(html);
  if (!/[<]/.test(input)) {
    return input;
  }

  if (typeof document === 'undefined') {
    return sanitizePlainText(input);
  }

  const template = document.createElement('template');
  template.innerHTML = input;
  sanitizeHtmlNode(template.content);

  const out = template.innerHTML;
  return hasPlainTextInjectionRisk(out) ? sanitizePlainText(input) : out;
}

function sanitizePlainText(value) {
  if (value == null) return '';
  return String(value)
    .replace(/<[^>]*>/g, '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .trim();
}

/**
 * Support-widget embed: keep script/iframe/link for legitimate widgets,
 * strip event handlers and javascript: URLs only.
 */
export function sanitizeEmbedHtml(html) {
  if (html == null || html === '') return '';

  let out = String(html);

  out = out.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, (match) => {
    return match
      .replace(/\s+on[a-zA-Z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '')
      .replace(/\b(javascript|vbscript)\s*:/gi, 'blocked:');
  });

  out = out.replace(/\s+on[a-zA-Z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '');
  out = out.replace(/(\s(?:href|src|xlink:href|formaction)\s*=\s*["']?\s*)(javascript|vbscript)\s*:/gi, '$1blocked:');
  out = out.replace(/(\s(?:href|src|xlink:href|formaction)\s*=\s*["'])javascript:/gi, '$1blocked:');

  out = out.replace(/<\s*(object|embed|svg|math|base|form)\b[^>]*>[\s\S]*?<\/\1>/gi, '');
  out = out.replace(/<\s*(object|embed|svg|math|base|form)\b[^>]*\/?>/gi, '');

  return out;
}

export function sanitizeModuleNameLink(comsetVal) {
  if (!comsetVal || typeof comsetVal !== 'object') return comsetVal;

  const modules = ['local', 'locator', 'enhance', 'b2b', 'simplifi', 'predict', 'clean', 'audience'];
  const copy = { ...comsetVal };

  modules.forEach((key) => {
    if (!copy[key] || typeof copy[key] !== 'object') return;
    copy[key] = {
      ...copy[key],
      name: copy[key].name != null ? sanitizeHtmlForDisplay(copy[key].name) : copy[key].name,
    };
  });

  return copy;
}
